import type { CropBox } from './types'

/**
 * Геометрия проверки обрезки (RP-FB73): рамка в пикселях выреза, зум и сдвиг картинки в окне просмотра. Чистые функции — без DOM, поэтому
 * проверяются обычными тестами. Правила рамки те же, что у службы media (`/crop-box`): внутри выреза, каждая сторона не меньше `CROP_MIN_SIDE`;
 * последнее слово за сервером — он отвергает неверную рамку словами.
 */

/** Не уже этой стороны (px выреза): как `MEDIA_CROP_BOX_MIN_SIDE` по умолчанию. */
export const CROP_MIN_SIDE = 8

export const ZOOM_MIN = 1
export const ZOOM_MAX = 8
/** Шаг кнопок «+» и «−» и одного щелчка колеса. */
export const ZOOM_STEP = 1.25

export type CropHandle = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se'

export const CROP_HANDLES: readonly CropHandle[] = ['nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e']

/** Ручка по значению атрибута; незнакомое значение — `null`. */
export function parseHandle(value: string | undefined): CropHandle | null {
  return CROP_HANDLES.find((handle) => handle === value) ?? null
}

/** Слова для читалки экрана и подсказок: какую границу или угол берёт ручка. */
export const HANDLE_LABELS: Readonly<Record<CropHandle, string>> = {
  n: 'Верхняя граница рамки',
  s: 'Нижняя граница рамки',
  w: 'Левая граница рамки',
  e: 'Правая граница рамки',
  nw: 'Левый верхний угол рамки',
  ne: 'Правый верхний угол рамки',
  sw: 'Левый нижний угол рамки',
  se: 'Правый нижний угол рамки',
}

export interface CropFrame {
  width: number
  height: number
}

const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value))

export const boxWidth = (box: CropBox): number => box.right - box.left
export const boxHeight = (box: CropBox): number => box.bottom - box.top

export const sameBox = (a: CropBox, b: CropBox): boolean => a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom

/** Рамка во весь вырез. */
export const wholeFrame = (frame: CropFrame): CropBox => ({ left: 0, top: 0, right: frame.width, bottom: frame.height })

/** Рамка внутри выреза и не уже минимума; целые пиксели. Вырез меньше минимума — рамка во весь вырез. */
export function fitBox(box: CropBox, frame: CropFrame): CropBox {
  if (frame.width < CROP_MIN_SIDE || frame.height < CROP_MIN_SIDE) return wholeFrame(frame)
  const left = clamp(Math.round(box.left), 0, frame.width - CROP_MIN_SIDE)
  const top = clamp(Math.round(box.top), 0, frame.height - CROP_MIN_SIDE)
  return {
    left,
    top,
    right: clamp(Math.round(box.right), left + CROP_MIN_SIDE, frame.width),
    bottom: clamp(Math.round(box.bottom), top + CROP_MIN_SIDE, frame.height),
  }
}

/** Сдвинуть рамку целиком, не меняя размера; за край выреза она не уходит. */
export function moveBox(box: CropBox, dx: number, dy: number, frame: CropFrame): CropBox {
  const width = boxWidth(box)
  const height = boxHeight(box)
  const left = clamp(box.left + Math.round(dx), 0, Math.max(0, frame.width - width))
  const top = clamp(box.top + Math.round(dy), 0, Math.max(0, frame.height - height))
  return { left, top, right: left + width, bottom: top + height }
}

/** Потянуть за угол или сторону: двигаются только границы этой ручки; рамка остаётся внутри выреза и не уже минимума. */
export function resizeBox(box: CropBox, handle: CropHandle, dx: number, dy: number, frame: CropFrame): CropBox {
  const stepX = Math.round(dx)
  const stepY = Math.round(dy)
  let { left, top, right, bottom } = box
  if (handle.includes('w')) left = clamp(left + stepX, 0, right - CROP_MIN_SIDE)
  if (handle.includes('e')) right = clamp(right + stepX, left + CROP_MIN_SIDE, frame.width)
  if (handle.includes('n')) top = clamp(top + stepY, 0, bottom - CROP_MIN_SIDE)
  if (handle.includes('s')) bottom = clamp(bottom + stepY, top + CROP_MIN_SIDE, frame.height)
  return { left, top, right, bottom }
}

/**
 * Сторона итогового квадрата: длинная сторона рамки плюс отступ с двух сторон, но не больше предела `maxSide` (больше — кадр уменьшается,
 * предмет не увеличивается никогда). Те же правила применяет media при сохранении.
 */
export function resultSide(box: CropBox, margin: number, maxSide: number): number {
  return Math.min(Math.max(boxWidth(box), boxHeight(box)) + 2 * margin, maxSide)
}

/** Итоговый кадр будет уменьшен (предмет с отступами не помещается в предел). */
export function willShrink(box: CropBox, margin: number, maxSide: number): boolean {
  return Math.max(boxWidth(box), boxHeight(box)) + 2 * margin > maxSide
}

export const clampZoom = (zoom: number): number => clamp(zoom, ZOOM_MIN, ZOOM_MAX)

/**
 * Доли квадратного окна просмотра, которые занимает вырез при зуме 1: длинная сторона — всё окно, короткая — пропорционально. Сдвиг картинки
 * измеряется в тех же долях окна: 0 — вырез по центру.
 */
export function fitOf(frame: CropFrame): { fitW: number; fitH: number } {
  const longest = Math.max(frame.width, frame.height, 1)
  return { fitW: frame.width / longest, fitH: frame.height / longest }
}

export interface Pan {
  x: number
  y: number
}

/** Сдвиг не уводит картинку из окна совсем: центр выреза остаётся в пределах половины его размера при текущем зуме. */
export function clampPan(pan: Pan, zoom: number, frame: CropFrame): Pan {
  const { fitW, fitH } = fitOf(frame)
  const limitX = (fitW * zoom) / 2
  const limitY = (fitH * zoom) / 2
  return { x: clamp(pan.x, -limitX, limitX), y: clamp(pan.y, -limitY, limitY) }
}

/** Новый зум при том же центре окна: сдвиг масштабируется вместе с картинкой, точка в центре остаётся на месте. */
export function zoomAround(pan: Pan, from: number, to: number, frame: CropFrame): { zoom: number; pan: Pan } {
  const zoom = clampZoom(to)
  const ratio = from === 0 ? 1 : zoom / from
  return { zoom, pan: clampPan({ x: pan.x * ratio, y: pan.y * ratio }, zoom, frame) }
}

/** Пикселей выреза в одном пикселе экрана: длинная сторона выреза занимает окно, умноженное на зум. */
export function imagePixelsPerScreenPixel(frame: CropFrame, zoom: number, stagePx: number): number {
  return stagePx > 0 ? Math.max(frame.width, frame.height) / (zoom * stagePx) : 0
}

/** JPEG выреза из base64 ответа сервера — для показа (адрес объекта на устройстве) и для отправки обратно вместе с рамкой. */
export function cutoutBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return new Blob([bytes], { type: contentType })
}
